import { useTranslation } from 'react-i18next';
const SLCLogo = '/logos/slc-logo-login.png';
import { User, Loader2, X } from 'lucide-react';
import LanguageSelector from '@/shared/components/LanguageSelector';
import { useState, useCallback } from 'react';
import ForgotPasswordLayout from '@/features/auth/components/modals/ForgotPasswordLayout';
import TwoFactorDialogue from '@/features/auth/components/modals/TwoFactorDialogue';
import { Button } from '@/shared/ui/elements/button/Button';
import { Input } from '@/shared/ui/elements/input/Input';
import { PasswordInput } from '@/shared/ui/elements/input/PasswordInput';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { useEffect } from 'react';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import VerifyEmailModal from '@/shared/components/modals/VerifyEmailModal';
import { useGenerateEsimQrMutation } from '@/features/esimQr/api/esimQrApi';
import type { GenerateEsimQrResponse } from '@/features/esimQr/api/esimQrApi';
import {
  useLookupLoginMethodMutation,
  useResetPasswordRequestMutation,
} from '@/features/auth/api/authApi';
import type {
  LoginMethodLookupResponse,
  LoginResponse,
} from '@/features/auth/api/authApi';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/ui/elements/dialog/Dialog';

interface ValidationErrors {
  username?: string;
  password?: string;
}

function LoginRightSection() {
  const { t } = useTranslation('login');

  const [isTFAmodalOpen, setIsTFAmodalOpen] = useState(false);
  const [isForgotPwdOpen, setIsForgotPwdOpen] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpId, setOtpId] = useState<string | null>(null);
  const [otpMethodCode, setOtpMethodCode] = useState<string | null>(null);
  const [isVerifyEmailOpen, setIsVerifyEmailOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrPayload, setQrPayload] = useState<GenerateEsimQrResponse | null>(
    null
  );

  const [loginMethodData, setLoginMethodData] =
    useState<LoginMethodLookupResponse | null>(null);
  const [showLoginMethodUI, setShowLoginMethodUI] = useState(false);
  const [resetLinkMessage, setResetLinkMessage] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{
    username?: boolean;
    password?: boolean;
  }>({});

  // Track if username is non-empty for showing clear button
  const isUsernameFilled = !!username.trim();

  const navigate = useNavigate();
  const [generateEsimQr, { isLoading: qrLoading }] =
    useGenerateEsimQrMutation();
  const [lookupLoginMethod, { isLoading: isLookingUpLoginMethod }] =
    useLookupLoginMethodMutation();
  const [requestResetPasswordLink, { isLoading: isSendingResetLink }] =
    useResetPasswordRequestMutation();
  const { login, verifyOtp, isLoading } = useAuth();
  const { handleError } = useErrorHandler();

  const validateUsername = (value: string): string | undefined => {
    if (!value.trim()) return 'Username or email is required';
    return undefined;
  };

  // const validatePassword = (value: string): string | undefined => {
  //   if (!value.trim()) return 'Password is required';
  //   if (value.length < 6) return 'Password must be at least 6 characters';
  //   return undefined;
  // };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    if (touched.username) {
      setErrors((prev) => ({ ...prev, username: validateUsername(value) }));
    }
  };

  const handleUsernameBlur = () => {
    setTouched((prev) => ({ ...prev, username: true }));
    setErrors((prev) => ({ ...prev, username: validateUsername(username) }));
  };

  const buildLoginPayload = (username: string, password: string) => {
    return { username, password };
  };

  const handleLoginResponse = useCallback(
    async (
      response: LoginResponse,
      loginMethodCode?: string | null
    ): Promise<void> => {
      switch (response.type) {
        case 'otp':
          setOtpMethodCode(loginMethodCode ?? 'EMAIL_OTP');
          setOtpId(response.otpId);
          setIsTFAmodalOpen(true);
          setShowLoginMethodUI(false);
          setLoginMethodData(null);
          return;

        case 'emailVerification':
          setIsVerifyEmailOpen(true);
          setShowLoginMethodUI(false);
          setLoginMethodData(null);
          return;

        case 'esimProvision': {
          const qrResult = await generateEsimQr({
            email: username,
            signature: response.signature,
          }).unwrap();

          setQrPayload(qrResult);
          setQrModalOpen(true);
          setShowLoginMethodUI(false);
          setLoginMethodData(null);
          return;
        }

        case 'login':
          setShowLoginMethodUI(false);
          setLoginMethodData(null);
          navigate('/', { replace: true });
          return;

        default:
          return;
      }
    },
    [generateEsimQr, navigate, username]
  );

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const usernameError = validateUsername(username);

    setErrors({ username: usernameError });
    setTouched({ username: true });
    if (usernameError) return;

    try {
      const loginMethodResponse = await lookupLoginMethod({
        username,
      }).unwrap();
      //if no loginMethodCode, for support user, goto password page. else send email otp
      const loginMethodCode =
        loginMethodResponse?.loginMethodCode ??
        (username.trim().toLowerCase() === 'support@slc.digital'
          ? 'PASSWORD'
          : 'EMAIL_OTP');

      // Check if it's SSO method with redirect
      if (
        loginMethodResponse.isSso &&
        loginMethodResponse.redirectUrl &&
        loginMethodResponse.state
      ) {
        // Save loginMethodCode and state to Redux via login
        // Then redirect to SSO provider
        setIsRedirecting(true);
        window.location.href = loginMethodResponse.redirectUrl;
        return;
      }

      setLoginMethodData(loginMethodResponse);

      const needsPassword = ![
        'EMAIL_OTP',
        'ESIM_OTP',
        'esimProvision',
      ].includes(loginMethodCode);

      if (needsPassword) {
        setShowLoginMethodUI(true);
        setPassword('');
        setResetLinkMessage(null);
        return;
      }

      const response = await login({ username });
      await handleLoginResponse(response, loginMethodCode);
    } catch (error) {
      handleError(error);
    }
  };

  const handleSendResetLink = useCallback(async () => {
    const usernameError = validateUsername(username);
    if (usernameError) {
      setErrors((prev) => ({ ...prev, username: usernameError }));
      setTouched((prev) => ({ ...prev, username: true }));
      return;
    }

    try {
      await requestResetPasswordLink({ username }).unwrap();
      setResetLinkMessage('Reset link has been sent to your email');
    } catch (error) {
      setResetLinkMessage(null);
      handleError(error);
    }
  }, [username, requestResetPasswordLink, handleError]);

  useEffect(() => {
    if (!resetLinkMessage) return;

    const timeoutId = window.setTimeout(() => {
      setResetLinkMessage(null);
      setShowLoginMethodUI(false);
      setLoginMethodData(null);
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [resetLinkMessage]);

  const handleProceedWithLogin = useCallback(async () => {
    if (!password.trim()) {
      setErrors({ password: 'Password is required' });
      return;
    }

    try {
      const payload = buildLoginPayload(username, password);
      const response = await login(payload);
      await handleLoginResponse(response, loginMethodData?.loginMethodCode);
    } catch (error) {
      handleError(error);
    }
  }, [
    username,
    password,
    login,
    handleError,
    handleLoginResponse,
    loginMethodData,
  ]);

  const handleVerifyOtp = async (code: string) => {
    if (!otpId) return;

    try {
      await verifyOtp(otpId, code, username);

      setIsTFAmodalOpen(false);
      setOtpId(null);
      setOtpMethodCode(null);

      navigate('/', { replace: true });
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center bg-lt-white p-8">
      <LanguageSelector />

      <div className="w-full max-w-md xl:px-6 2xl:px-0">
        <div className="flex justify-center items-center mb-8 py-4 rounded-xl">
          <img
            src={SLCLogo}
            alt="SLC Logo"
            className="2xl:h-20 xl:h-18 w-auto object-contain"
          />
        </div>

        <div className="mb-6">
          <p className="text-lt-slate-500 text-base">{t('signInAccount')}</p>
        </div>

        {!showLoginMethodUI ? (
          <form className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-lt-slate-700">
                {t('usernameLabel')}
              </label>

              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-lt-slate-400" />
                <Input
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  onBlur={handleUsernameBlur}
                  placeholder="example@slc.digital"
                  error={
                    errors.username && touched.username
                      ? errors.username
                      : undefined
                  }
                  className="pl-12 w-full h-12 rounded-full border border-slate-200"
                />
                {isUsernameFilled && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-lt-slate-100 hover:bg-lt-slate-200 transition-colors flex items-center justify-center"
                    onClick={() => setUsername('')}
                    aria-label={t('clear')}
                    tabIndex={0}
                  >
                    <X className="w-5 h-5 text-lt-slate-500 cursor-pointer" />
                  </button>
                )}
              </div>

              {errors.username && touched.username && (
                <p className="text-xs text-red-600 ml-2">{errors.username}</p>
              )}
            </div>

            <Button
              variant="login"
              size="xl"
              rounded="full"
              onClick={handleLogin}
              disabled={isLoading || isLookingUpLoginMethod || qrLoading}
            >
              {isLoading || isLookingUpLoginMethod || qrLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('waitButton')}
                </>
              ) : (
                t('continueButton')
              )}
            </Button>
          </form>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2 mb-1">
              <label className="text-sm font-medium text-lt-slate-700">
                {t('passwordLabel')}
              </label>
              <PasswordInput
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) {
                    setErrors((prev) => ({ ...prev, password: undefined }));
                  }
                }}
                placeholder={t('passwordEntryPlaceholder')}
                className="pl-4 w-full h-12 rounded-full border border-slate-200"
              />
              {errors.password && (
                <p className="text-xs text-red-600 ml-2">{errors.password}</p>
              )}
            </div>

            {resetLinkMessage ? (
              <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {resetLinkMessage}
              </div>
            ) : (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSendResetLink}
                  disabled={isSendingResetLink}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:cursor-pointer hover:underline disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                  {isSendingResetLink ? t('waitButton') : t('forgotPassword')}
                </button>
              </div>
            )}

            <Button
              variant="login"
              size="xl"
              rounded="full"
              onClick={handleProceedWithLogin}
              disabled={isLoading || qrLoading || isSendingResetLink}
            >
              {isLoading || qrLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('loginButton')}
                </>
              ) : (
                t('loginButton')
              )}
            </Button>

            <Button
              variant="whiteButton"
              size="xl"
              rounded="full"
              onClick={() => {
                setShowLoginMethodUI(false);
                setLoginMethodData(null);
              }}
              disabled={isLoading || qrLoading}
              className="border border-slate-200"
            >
              {t('back')}
            </Button>
          </div>
        )}
      </div>

      {isForgotPwdOpen && (
        <ForgotPasswordLayout onClose={() => setIsForgotPwdOpen(false)} />
      )}

      {/*  OTP MODAL WIRED CORRECTLY */}
      {isTFAmodalOpen && otpId && (
        <TwoFactorDialogue
          username={username}
          onClose={() => {
            setIsTFAmodalOpen(false);
            setOtpId(null);
            setOtpMethodCode(null);
          }}
          onVerify={handleVerifyOtp}
          onOtpIdUpdate={(newOtpId) => setOtpId(newOtpId)}
          instructionText={
            otpMethodCode === 'ESIM_OTP'
              ? t('otpSentToPhone')
              : t('otpSentToEmail')
          }
          methodBadgeLabel={
            otpMethodCode === 'ESIM_OTP' ? t('mobileOtpOption') : undefined
          }
        />
      )}

      {isVerifyEmailOpen && (
        <VerifyEmailModal
          open={isVerifyEmailOpen}
          onClose={() => setIsVerifyEmailOpen(false)}
          email={username}
        />
      )}

      {/* eSIM QR Dialog - new design */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="bg-white p-0 border-none max-w-2xl">
          <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 p-6 shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
            <div className="flex items-center justify-between mb-6">
              <DialogTitle className="text-2xl font-bold text-slate-900">
                Scan the QR Code
              </DialogTitle>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-10 mb-8">
              <div className="flex flex-col items-center">
                <div className="relative bg-white border border-gray-200 rounded-xl shadow-sm p-4 inline-flex min-h-[192px] min-w-[192px]">
                  {qrLoading ? (
                    <Loader2 className="w-12 h-12 animate-spin text-slate-400" />
                  ) : (
                    qrPayload?.qrCode && (
                      <img
                        src={qrPayload.qrCode}
                        alt="eSIM QR"
                        className="w-48 h-48"
                      />
                    )
                  )}
                </div>
                {/* Show iOS and Android install deeplinks directly below the QR code */}
                <div className="mt-6 text-center space-y-2">
                  {qrPayload?.iosDeeplink && (
                    <div className="text-xs text-slate-700">
                      iOS Install Link:{' '}
                      <a
                        href={qrPayload.iosDeeplink}
                        className="text-blue-700 underline hover:text-blue-900"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Click to install on iOS
                      </a>
                    </div>
                  )}
                  {qrPayload?.androidDeeplink && (
                    <div className="text-xs text-slate-700">
                      Android Install Link:{' '}
                      <a
                        href={qrPayload.androidDeeplink}
                        className="text-green-700 underline hover:text-green-900"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Click to install on Android
                      </a>
                    </div>
                  )}
                </div>
                {/* Info content below the links */}
                <div className="mt-4 text-center max-w-md">
                  <p className="text-sm text-slate-700 mb-2 text-center">
                    Use the links above to install your eSIM.
                  </p>
                  <p className="text-xs text-slate-500 mt-3 text-center">
                    This QR code is single-use. Do not share it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {isRedirecting && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
            <p className="text-lg font-medium text-slate-900">
              {t('redirectingToSSO')}
            </p>
            <p className="text-sm text-slate-500">
              {t('pleaseDontCloseWindow')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginRightSection;
